import { useAuth } from '../components/Auth/AuthContext/AuthContext';

const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

interface ErrorBody {
  message?: string;
  error?: string;
}

const parseErrorBody = (text: string): ErrorBody | null => {
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

export class ApiClientError extends Error {
  readonly status: number;
  readonly details?: unknown;

  private constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.details = details;
  }

  static async fromResponse(response: Response): Promise<ApiClientError> {
    // the backend error handlers do not always send their JSON body with a JSON content type
    const text = await response.text().catch(() => '');
    const errorData = parseErrorBody(text);

    if (errorData) {
      return new ApiClientError(
        errorData.message || errorData.error || response.statusText,
        response.status,
        errorData,
      );
    }

    return new ApiClientError(text || response.statusText, response.status);
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isServerError(): boolean {
    return this.status >= 500;
  }
}

export const useApiClient = () => {
  const { getAccessTokenSilently } = useAuth();

  return async <T>(endpoint: string, options: RequestInit = {}): Promise<T extends void ? void : T> => {
    const sendRequest = async (cacheMode: 'on' | 'off') => {
      const token = await getAccessTokenSilently({ cacheMode });

      const headers = new Headers(options.headers);

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      } else {
        headers.delete('Authorization');
      }

      if (options.body instanceof FormData) {
        headers.delete('Content-Type');
      } else if (options.body) {
        headers.set('Content-Type', 'application/json');
      }

      return fetch(`${backendUrl}${endpoint}`, { ...options, headers });
    };

    let response = await sendRequest('on');

    if (response.status === 401) {
      response = await sendRequest('off');
    }

    if (!response.ok) {
      throw await ApiClientError.fromResponse(response);
    }

    if (response.status === 204) {
      return undefined as T extends void ? void : T;
    }

    const contentType = response.headers.get('content-type') ?? '';

    if (contentType.includes('application/json')) {
      return response.json();
    }

    await response.text();
    return undefined as T extends void ? void : T;
  };
};
